import React, { useCallback, useEffect, useState, useRef } from "react";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import api from "../../services/api";
import { format } from "date-fns";

// Every conversation with the 866 assistants: the promoter assistant and the venue brain.
// Texts both ways, with what the assistant did and any error inline. Read-only.
const ASSISTANT_LABELS = { promoter: "Promoter assistant", venue: "Venue assistant" };

const ACTION_LABELS = {
  posted_plan: "Posted plans",
  edited_plan: "Edited a plan",
  edited_night: "Edited a weekly night",
  accepted_request: "Accepted requests",
  declined_request: "Declined requests",
  took_down_plan: "Took down a plan",
  restarted_night: "Restarted a weekly night",
  stopped_night: "Stopped a weekly night",
  request_alert: "Sent a request alert",
  confirmed_table: "Confirmed a table",
  declined_table: "Declined a table",
  offered_other_table: "Offered another table",
  no_reply_by_design: "No reply, by design",
};

const ERROR_LABELS = {
  ai_timeout: "AI timed out",
  ai_error: "AI provider error",
  ai_fallback_used: "AI unavailable, plain-code reader answered",
  tool_error: "Something failed",
  validation_rejected: "Turned down",
  unknown_intent: "Couldn't do what was asked",
  send_failed: "Text failed to send",
  inbound_no_reply: "No reply was sent",
};

const TOOL_LABELS = {
  list_plans: "my plans", post_nights: "post nights", edit_plan: "edit a plan", guests: "guests",
  decide_requests: "accept or decline", share_link: "share link", stats: "stats", skip_night: "skip a night",
  restart_night: "restart a night", stop_night: "stop a night", help: "help", other: "something else",
};

const formatDate = (d, f = "MMM d, h:mm a") => {
  if (!d) return "";
  try { return format(new Date(d), f); } catch { return ""; }
};

const shortId = (id) => String(id).slice(-6);

const actionLabel = (name) => {
  if (ACTION_LABELS[name]) return ACTION_LABELS[name];
  if (String(name).startsWith("venue_")) return `Venue assistant: ${String(name).slice(6).replace(/_/g, " ")}`;
  return String(name).replace(/_/g, " ");
};

// ZONA AI's read of a promoter text (read_nights / read_match / read_correction / venue_answer rows), as plain lines.
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const clock = (min) => {
  if (min === null || min === undefined || min === "") return null;
  const n = Number(min);
  if (!Number.isFinite(n)) return String(min);
  const h = Math.floor(n / 60) % 24;
  const mm = n % 60;
  return `${h % 12 || 12}${mm ? `:${String(mm).padStart(2, "0")}` : ""}${h < 12 ? "am" : "pm"}`;
};
// "2026-10-24" -> "Sat Oct 24" (read as a local date, so it never shifts a day).
const isoDay = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso));
  return m ? formatDate(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])), "EEE MMM d") : String(iso);
};
const nightLine = (g) => [
  g.venue,
  [...(g.days || []).map((d) => DAYS[d] ?? d), ...(g.dates || []).map(isoDay)].join(", ") || null,
  clock(g.start) && `${clock(g.start)}${clock(g.end) ? `-${clock(g.end)}` : ""}`,
  clock(g.closes) && `closes ${clock(g.closes)}`,
  g.kind,
].filter(Boolean).join(" · ");
const via = (source) => (source ? `, via ${source === "fallback" ? "plain code" : source}` : "");
const raw = (r) => (r.raw ? `Model said: ${JSON.stringify(r.raw).slice(0, 600)}` : null);
const readLines = (m) => {
  const r = m.read || {};
  if (r.uncopyable) return [`${actionLabel(m.action)}: not kept (the read couldn't be saved)`];
  if (m.action === "read_nights") {
    const groups = r.groups || [];
    const head = r.action === "create" ? `Read ${groups.length} night line${groups.length === 1 ? "" : "s"}` : `Read it as "${r.action || "nothing"}"`;
    return [head, ...groups.map(nightLine), r.action !== "create" ? raw(r) : null].filter(Boolean);
  }
  if (m.action === "read_match") {
    return ["Matched venues", ...(r.venues || []).map((v) => {
      const week = v.in_coming_week === false ? " (not in the coming week)" : "";
      if (v.venue_name) return `${v.read} → ${v.venue_name}${v.venue_id ? "" : " (not on ZONA)"}${week}`;
      return (v.options || []).length ? `${v.read} → unsure, asks him: ${v.options.join(" / ")}${week}` : `${v.read} → no match${week}`;
    })];
  }
  if (m.action === "venue_answer") {
    return [`Venue answer "${r.answer}" for ${r.read}`, `→ ${r.venue_name || "still unsure"}${r.venue_name && !r.venue_id ? " (not on ZONA)" : ""}${(r.offered || []).length ? ` (offered: ${r.offered.join(" / ")})` : ""}`];
  }
  if (m.action === "read_correction") {
    const res = r.result;
    if (!res) return ["Couldn't read the change"];
    if (res.newList) return ["Read it as a new list"];
    const night = (n) => {
      const l = (r.lines || []).find((x) => x.n === n);
      return l ? `${l.venue} ${l.day}` : `Night ${n}`;
    };
    if (!(res.changes || []).length && !(res.add || []).length) return ["Read no change", raw(r)].filter(Boolean);
    const ch = (res.changes || []).map((c) => `${night(c.n)}: ${c.remove ? "removed" : [clock(c.start) && `starts ${clock(c.start)}`, clock(c.end) && `ends ${clock(c.end)}`, clock(c.closes) && `closes ${clock(c.closes)}`].filter(Boolean).join(", ")}`);
    return ["Read the change", ...ch, ...(res.add || []).map((g) => `Add: ${nightLine(g)}`)];
  }
  return null;
};

const AssistantConversations = () => {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assistant, setAssistant] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);

  const params = useCallback(() => ({
    ...(assistant ? { assistant } : {}),
    // Plain dates: the server reads them as whole days in New York.
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  }), [assistant, from, to]);

  // Only the newest list request may update the list (typing fires one per keystroke).
  const listRequest = useRef(0);
  const fetchThreads = useCallback(async () => {
    const mine = ++listRequest.current;
    try {
      setLoading(true);
      const { data } = await api.get(`/admin/assistant-866/conversations`, {
        params: { ...params(), ...(errorsOnly ? { errors: "1" } : {}), ...(q ? { q } : {}) },
      });
      if (mine === listRequest.current) setThreads(data?.data || []);
    } catch (err) {
      console.error("Failed to fetch 866 conversations:", err);
      if (mine === listRequest.current) setThreads([]);
    } finally {
      if (mine === listRequest.current) setLoading(false);
    }
  }, [params, errorsOnly, q]);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  useEffect(() => {
    if (!selected) { setThread(null); return undefined; }
    // Ignore a slower response for a person who's no longer selected.
    let live = true;
    setThreadLoading(true);
    api.get(`/admin/assistant-866/conversations/${selected}`, { params: params() })
      .then(({ data }) => { if (live) setThread(data?.data || null); })
      .catch((err) => { if (live) { console.error("Failed to fetch the conversation:", err); setThread(null); } })
      .finally(() => { if (live) setThreadLoading(false); });
    return () => { live = false; };
  }, [selected, params]);

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content" style={{ padding: 24 }}>
          <div style={{ width: "100%", marginBottom: 16 }}>
            <h1 style={{ margin: 0, color: "#fff" }}>866 Conversations</h1>
            <p style={{ margin: "4px 0 0", color: "rgba(255,255,255,0.5)", fontSize: 13 }}>
              What promoters and venue staff texted the 866, what it answered, what it did, and anything that went wrong.
            </p>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
            <input type="text" placeholder="Search a name or last 4 digits" value={q} onChange={(e) => setQ(e.target.value)} style={{ ...field, width: 240 }} />
            <select value={assistant} onChange={(e) => setAssistant(e.target.value)} style={field}>
              <option value="">Both assistants</option>
              <option value="promoter">Promoter assistant</option>
              <option value="venue">Venue assistant</option>
            </select>
            <label style={label}>From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={field} /></label>
            <label style={label}>To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={field} /></label>
            <label style={label}>
              <input type="checkbox" checked={errorsOnly} onChange={(e) => setErrorsOnly(e.target.checked)} /> Only people with errors
            </label>
          </div>

          <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div style={{ ...panel, flex: "1 1 320px", maxWidth: 420 }}>
              {loading ? (
                <div style={empty}>Loading…</div>
              ) : threads.length === 0 ? (
                <div style={empty}>No conversations yet.</div>
              ) : threads.map((t) => (
                <button
                  key={t.thread_id}
                  type="button"
                  onClick={() => setSelected(t.thread_id)}
                  style={{ ...row, background: selected === t.thread_id ? "rgba(255,255,255,0.08)" : "transparent" }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{t.name}</span>
                    <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", whiteSpace: "nowrap" }}>{formatDate(t.last_at)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", marginTop: 2 }}>
                    {t.role} · {t.phone}
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>
                    {t.assistants.map((a) => ASSISTANT_LABELS[a] || a).join(" and ")} · {t.texts_in} in, {t.texts_out} out, {t.actions} actions
                    {t.errors > 0 && <span style={{ color: "#F87171" }}> · {t.errors} {t.errors === 1 ? "error" : "errors"}</span>}
                  </div>
                </button>
              ))}
            </div>

            <div style={{ ...panel, flex: "2 1 480px", padding: 16, minHeight: 300 }}>
              {!selected ? (
                <div style={empty}>Pick a person to read the conversation.</div>
              ) : threadLoading ? (
                <div style={empty}>Loading…</div>
              ) : !thread ? (
                <div style={empty}>Couldn't load this conversation.</div>
              ) : (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 600, fontSize: 16, color: "#fff" }}>{thread.name}</div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>{thread.role} · {thread.phone}</div>
                  </div>
                  {thread.messages.length === 0 ? (
                    <div style={empty}>Nothing in this date range.</div>
                  ) : thread.messages.map((m) => <Entry key={m.id} m={m} />)}
                </>
              )}
            </div>
          </div>
        </article>
      </div>
    </main>
  );
};

const Entry = ({ m }) => {
  const time = formatDate(m.at, "MMM d, h:mm:ss a");
  if (m.direction === "event") {
    if (m.error) {
      return (
        <div style={{ ...eventLine, color: "#F87171" }}>
          {ERROR_LABELS[m.error.kind] || m.error.kind}
          {m.error.code ? ` (${m.error.code})` : ""}
          {m.error.message && m.error.kind !== "inbound_no_reply" ? `: ${m.error.message}` : ""}
          <span style={eventTime}>{time}</span>
        </div>
      );
    }
    if (m.action === "tool") {
      return (
        <div style={eventLine}>
          Read it as {TOOL_LABELS[m.intent] || m.intent}{via(m.source)}
          <span style={eventTime}>{time}</span>
        </div>
      );
    }
    const read = m.read ? readLines(m) : null;
    if (read) {
      return (
        <div style={{ ...eventLine, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {read[0]}{via(m.source)}
          <span style={eventTime}>{time}</span>
          {read.length > 1 && (
            <div style={{ textAlign: "left", display: "inline-block", maxWidth: "100%", marginTop: 2 }}>
              {read.slice(1).map((line, i) => <div key={i}>{line}</div>)}
            </div>
          )}
        </div>
      );
    }
    const ids = [
      ...(m.plan_ids || []).map((id) => `plan ${shortId(id)}`),
      ...(m.request_ids || []).map((id) => `request ${shortId(id)}`),
    ];
    return (
      <div style={eventLine}>
        {actionLabel(m.action)}{m.body ? `: ${m.body}` : ""}{ids.length ? ` (${ids.slice(0, 6).join(", ")}${ids.length > 6 ? ` and ${ids.length - 6} more` : ""})` : ""}
        <span style={eventTime}>{time}</span>
      </div>
    );
  }
  const mine = m.direction === "out";
  return (
    <div style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", margin: "8px 0" }}>
      <div style={{ maxWidth: "75%" }}>
        <div style={{
          background: mine ? "#0A3FD6" : "rgba(255,255,255,0.08)",
          color: "#fff", padding: "8px 12px", borderRadius: 14, fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word",
        }}>
          {m.body || (m.num_media > 0 ? `${m.num_media} picture${m.num_media === 1 ? "" : "s"}` : "")}
        </div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 3, textAlign: mine ? "right" : "left" }}>
          {mine ? "ZONA" : "Them"} · {ASSISTANT_LABELS[m.assistant] || m.assistant} · {time}{m.backfilled ? " · from Twilio history" : ""}
        </div>
      </div>
    </div>
  );
};

const field = { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 13, outline: "none" };
const label = { display: "flex", alignItems: "center", gap: 6, color: "rgba(255,255,255,0.7)", fontSize: 13 };
const panel = { background: "rgba(255,255,255,0.03)", borderRadius: 12, border: "1px solid rgba(255,255,255,0.06)", overflow: "hidden" };
const row = { display: "block", width: "100%", textAlign: "left", color: "#fff", border: "none", borderTop: "1px solid rgba(255,255,255,0.05)", padding: "12px 14px", cursor: "pointer" };
const empty = { padding: 32, textAlign: "center", color: "rgba(255,255,255,0.6)", fontSize: 13 };
const eventLine = { fontSize: 12, color: "rgba(255,255,255,0.6)", textAlign: "center", margin: "6px 0", padding: "4px 8px" };
const eventTime = { marginLeft: 8, color: "rgba(255,255,255,0.35)" };

export default AssistantConversations;
